import { type ForwardedRef, forwardRef } from 'react';

import { cva, type VariantProps } from 'cva';
import { Collection } from 'react-aria-components/Collection';
import {
  GridList,
  GridListLoadMoreItem,
  type GridListProps as RACGridListProps,
} from 'react-aria-components/GridList';

import { ProgressCircle } from '../progress_circle/progress_circle';

import styles from './list_view.module.css';

const listViewVariants = cva({
  base: styles.list_view,
  variants: {
    gap: {
      none: undefined,
      s: styles.gap_s,
      m: styles.gap_m,
      l: styles.gap_l,
      xl: styles.gap_xl,
      '2xl': styles.gap_2xl,
      '3xl': styles.gap_3xl,
      '4xl': styles.gap_4xl,
    },
  },
  defaultVariants: {
    gap: 's',
  },
});

export type ListViewProps<T> = RACGridListProps<T> &
  VariantProps<typeof listViewVariants> & {
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
  };

/**
 * ListView - Displays a one-dimensional vertical list of interactive items,
 * with support for keyboard navigation, single or multiple selection, and row actions.
 *
 * See `ListItem` for a default list row implementation.
 *
 * See https://react-spectrum.adobe.com/react-aria/GridList.html for more details.
 *
 * ```tsx
 * <ListView aria-label="Users" items={users}>
 *   {(user) => (
 *      <ListItem id={user.id} textValue={user.name}>
 *        <ListItemText>{user.name}</ListItemText>
 *        <ListItemDescription>{user.role}</ListItemDescription>
 *      </ListItem>
 *   )}
 * </ListView>
 * ```
 *
 * Pass `onLoadMore` to load pages as the end of the list is scrolled into view.
 */
export const ListView = forwardRef(
  <T extends object>(
    props: ListViewProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      children,
      className,
      gap,
      items,
      onLoadMore,
      isLoadingMore,
      ...rest
    } = props;

    const classes = listViewVariants({
      gap,
      className,
    });

    return (
      <GridList ref={ref} layout="stack" {...rest} className={classes}>
        <Collection items={items}>{children}</Collection>
        {onLoadMore ? (
          <GridListLoadMoreItem
            className={styles.load_more_item}
            onLoadMore={onLoadMore}
            isLoading={isLoadingMore}
          >
            <ProgressCircle size="s" aria-label="Loading more" />
          </GridListLoadMoreItem>
        ) : null}
      </GridList>
    );
  },
);
